import type { EmailTemplate } from "./types";

/**
 * By Email search: optional `[Email #][space][title fragment]`.
 * User codes: digits. System codes: S + digits (e.g. S012).
 * Valid: `4232 - wsg`, `S012 reset`, `9 - welcome`. Invalid: `4312Wsg` (no space after digits).
 */
export type ByEmailSearchParse =
  | { kind: "all" }
  | { kind: "invalid" }
  | { kind: "codeOnly"; code: string }
  | { kind: "codeAndTitle"; code: string; title: string }
  | { kind: "titleOnly"; title: string };

const EMAIL_CODE_WITH_TITLE = /^(S\d+|\d+)\s+(.*)$/i;
const EMAIL_CODE_ONLY = /^(S\d+|\d+)$/i;

export function parseByEmailSearchQuery(raw: string): ByEmailSearchParse {
  const q = raw.trim();
  if (!q) {
    return { kind: "all" };
  }

  const combined = EMAIL_CODE_WITH_TITLE.exec(q);
  if (combined) {
    return {
      kind: "codeAndTitle",
      code: combined[1],
      title: combined[2],
    };
  }

  if (EMAIL_CODE_ONLY.test(q)) {
    return { kind: "codeOnly", code: q };
  }

  if (/^\d/.test(q) || /^S\d/i.test(q)) {
    return { kind: "invalid" };
  }

  return { kind: "titleOnly", title: q };
}

function normEmailNumber(t: EmailTemplate): string {
  return String(t.emailNumber ?? "").trim();
}

/** Strip leading punctuation users type when mirroring "1003 - Title" while DB title is "Title". */
function stripLeadingTitleNoise(s: string): string {
  return s.replace(/^[\s\-–—:_]+/, "").trim();
}

/** Match stored email # against typed code; supports S-codes and leading-zero padding on digits. */
function emailNumberMatchesCode(numRaw: string, code: string): boolean {
  const num = numRaw.trim();
  const c = code.trim();
  if (!c) {
    return true;
  }
  if (num.toLowerCase().startsWith(c.toLowerCase())) {
    return true;
  }
  if (/^\d+$/.test(num) && /^\d+$/.test(c)) {
    const nStripped = num.replace(/^0+/, "") || "0";
    const cStripped = c.replace(/^0+/, "") || "0";
    return nStripped.startsWith(cStripped);
  }
  return false;
}

export function emailTemplateMatchesByEmailQuery(
  template: EmailTemplate,
  raw: string,
): boolean {
  const parsed = parseByEmailSearchQuery(raw);
  if (parsed.kind === "all") {
    return true;
  }
  if (parsed.kind === "invalid") {
    return false;
  }

  const num = normEmailNumber(template);
  const title = String(template.name ?? "");

  if (parsed.kind === "titleOnly") {
    return title.toLowerCase().includes(parsed.title.toLowerCase());
  }

  if (parsed.kind === "codeOnly") {
    return emailNumberMatchesCode(num, parsed.code);
  }

  const numOk = emailNumberMatchesCode(num, parsed.code);
  if (!numOk) {
    return false;
  }

  const tq = parsed.title.trim();
  if (!tq) {
    return true;
  }

  const hay = title.toLowerCase();
  const low = tq.toLowerCase();
  if (hay.includes(low)) {
    return true;
  }

  const stripped = stripLeadingTitleNoise(tq).toLowerCase();
  if (stripped && hay.includes(stripped)) {
    return true;
  }

  return false;
}
